import Dexie, { Table } from "dexie";
import { isMobile } from "./Device";

export interface Option {
  id?: number;

  text?: string; // 文本
  availableKeyCodes?: string[]; // 可用按键
  inputText?: string; // 输入文本

  height?: number;

  countUp?: number;

  showAvailableKeys?: boolean; // 是否显示可用按键
  showTargetKey?: boolean; // 是否显示目标按键
  showKeyboard?: boolean; // 是否显示键盘
  showPinyin?: boolean; // 是否显示拼音
  showCountUp?: boolean; // 是否显示计数器
  showStatisic?: boolean; // 是否显示统计

  closeAt?: number; // 关闭时间

  setupTypes?: string[];
  lastConfirmAt?: Date; // 上次确认时间
}

export interface KeyLog {
  key: string;
  status: "correct" | "wrong" | null;
}

export class StateDateDexie extends Dexie {
  options!: Table<Option>;
  keylogs!: Table<KeyLog>;

  private reconnectPromise: Promise<void> | null = null;
  private recoveryEventsBound = false;

  private async ensureConnection(reason: string) {
    if (this.isOpen()) {
      return;
    }
    await this.reconnect(reason);
  }

  private async reconnect(reason: string) {
    if (this.reconnectPromise) {
      return this.reconnectPromise;
    }

    this.reconnectPromise = (async () => {
      try {
        if (this.isOpen()) {
          return;
        }
        console.warn(`[Store] IndexedDB disconnected, reopening (${reason})`);
        await this.open();
      } catch (error) {
        console.error(`[Store] Failed to reopen IndexedDB (${reason})`, error);
        throw error;
      } finally {
        this.reconnectPromise = null;
      }
    })();

    return this.reconnectPromise;
  }

  private bindRecoveryEvents() {
    if (this.recoveryEventsBound || typeof window === "undefined") {
      return;
    }

    this.recoveryEventsBound = true;

    const recover = (reason: string) => {
      this.ensureConnection(reason).catch(() => undefined);
    };

    this.on("close", () => {
      recover("close-event");
    });

    window.addEventListener("pageshow", () => {
      recover("pageshow");
    });
    window.addEventListener("focus", () => {
      recover("window-focus");
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        recover("visibility-visible");
      }
    });
  }

  getOptions() {
    return this.ensureConnection("getOptions").then(() =>
      this.options.toCollection().first()
    );
  }

  async getStatics() {
    await this.ensureConnection("getStatics");
    const correctCount = await this.keylogs
      .where("status")
      .equals("correct")
      .count();
    const wrongCount = await this.keylogs
      .where("status")
      .equals("wrong")
      .count();
    const allCount = await this.keylogs.count();
    return [correctCount, wrongCount, allCount];
  }

  constructor() {
    super("jianguoke.type");
    this.version(4).stores({
      options: "++id",
      keylogs: "++id, status",
    });
    this.bindRecoveryEvents();
  }

  async init() {
    await this.ensureConnection("init");
    if ((await this.options.count()) <= 0) {
      await this.options.add({
        showKeyboard: !isMobile,
        showPinyin: true,
      });
    }
  }

  async setOption(key: string, value: any) {
    await this.ensureConnection("setOption");
    const existing = await this.getOptions();
    if (existing) {
      await this.options.update(existing.id!, {
        [key]: value,
      });
    } else {
      await this.options.add({
        [key]: value,
      });
    }
  }

  async clearLog(status?: "correct" | "wrong" | null | undefined) {
    await this.ensureConnection("clearLog");
    if (!status) {
      await this.keylogs.clear();
    } else {
      await this.keylogs.where("status").equals(status).delete();
    }
  }

  async addLog(key: string, status: "correct" | "wrong" | null) {
    await this.ensureConnection("addLog");
    await this.keylogs.add({
      key,
      status,
    });
  }

  async getLogs() {
    await this.ensureConnection("getLogs");
    return this.keylogs.toArray();
  }
}

const win = globalThis as typeof globalThis & {
  store?: StateDateDexie;
};
export const store = (win.store ??= new StateDateDexie());
